import { forwardRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import { Turnstile } from '@marsidev/react-turnstile'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input as CInput,
  InputGroup,
  InputProps,
  Link,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { emailLeadSales } from '@/api/contactSales'
import { TURNSTILE_KEY } from '@/constants'
import { EMAIL_PATTERN } from '@/utils/inputPattern'
import { PHONE_NUMBER_PATTERN } from '@/utils/inputPattern'
import { ContactSalesModal } from './ContactSalesModal'

type FormValues = {
  firstName: string
  lastName: string
  email: string
  phone: string
  smsAgreement: boolean
}

const Input = forwardRef(({ ...rest }: InputProps, ref) => {
  const bgColor = useColorModeValue('gray.40', 'black.200')
  return (
    <CInput
      ref={ref}
      fontSize={'13px'}
      bgColor={bgColor}
      border="1px solid rgba(0, 0, 0, 0.1)"
      borderRadius="10px"
      {...rest}
    />
  )
})

export function ContactSalesForm() {
  const t = useTranslations()
  const textColor = useColorModeValue('blackAlpha.900', 'whiteAlpha.900')
  const toast = useToast()
  const [captchaSuccess, setCaptchaSuccess] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      smsAgreement: false,
    },
  })

  // Watch for phone changes to conditionally require SMS agreement
  const phoneValue = watch('phone', '')

  const [sendLeadEmail, { loading }] = useMutation(emailLeadSales, {
    onCompleted: () => {
      onOpen()
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        smsAgreement: false,
      })
    },
    onError: () => {
      toast({
        title: t('accelerated.form-error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    },
  })

  const onSubmit = async (values: FormValues) => {
    sendLeadEmail({
      variables: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
      },
    })
  }

  return (
    <Box>
      <VStack
        spacing={{ base: '25px', md: '24px' }}
        color={textColor}
        css={{
          '& .chakra-form__label': {
            marginBottom: '5px',
          },
          '& .chakra-input': {
            ':focus-visible': {
              boxShadow: '0 0 0 1px black',
            },
          },
        }}
      >
        {/* 2 columns on desktop, 1 column on mobile */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={6}
          w="100%"
          alignItems={'flex-end'}
        >
          <FormControl id="firstName" isInvalid={!!errors?.firstName} w="full">
            <FormLabel fontSize={'12px'} lineHeight="normal">
              <HStack>
                <Text as="span">{t('contact-sales.form.first-name')}</Text>
                <FormErrorMessage
                  mt={0}
                  fontSize={'xs'}
                  textTransform="lowercase"
                >
                  {errors.firstName && errors.firstName.message}
                </FormErrorMessage>
              </HStack>
            </FormLabel>
            <Input
              type="text"
              placeholder="Satoshi"
              {...register('firstName', {
                required: t('forms.errors.is-required'),
              })}
              bg={useColorModeValue('blackAlpha.200', 'whiteAlpha.200')}
              borderColor={useColorModeValue(
                'blackAlpha.200',
                'whiteAlpha.200'
              )}
            />
          </FormControl>
          <FormControl id="lastName" isInvalid={!!errors?.lastName} w="full">
            <FormLabel fontSize={'12px'} lineHeight="normal">
              <HStack>
                <Text as="span">{t('contact-sales.form.last-name')}</Text>
                <FormErrorMessage
                  mt={0}
                  fontSize={'xs'}
                  textTransform="lowercase"
                >
                  {errors.lastName && errors.lastName.message}
                </FormErrorMessage>
              </HStack>
            </FormLabel>
            <Input
              type="text"
              placeholder="Nakomoto"
              {...register('lastName', {
                required: t('forms.errors.is-required'),
              })}
              bg={useColorModeValue('blackAlpha.200', 'whiteAlpha.200')}
              borderColor={useColorModeValue(
                'blackAlpha.200',
                'whiteAlpha.200'
              )}
            />
          </FormControl>
        </Flex>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={6}
          w="100%"
          alignItems={'flex-end'}
        >
          <FormControl id="email" isInvalid={!!errors?.email} w="full">
            <FormLabel fontSize={'12px'} lineHeight="normal">
              <HStack>
                <Text as="span">{t('contact-sales.form.email')}</Text>
                <FormErrorMessage
                  mt={0}
                  fontSize={'xs'}
                  textTransform="lowercase"
                >
                  {errors.email &&
                    (errors.email.message ||
                      t('contact-sales.form.invalid-format'))}
                </FormErrorMessage>
              </HStack>
            </FormLabel>
            <InputGroup size="md">
              <Input
                type={'text'}
                placeholder="satoshi@bitcoin.org"
                {...register('email', {
                  required: t('forms.errors.is-required'),
                  validate: {
                    format: (val) => {
                      return EMAIL_PATTERN.test(val)
                    },
                  },
                })}
                bg={useColorModeValue('blackAlpha.200', 'whiteAlpha.200')}
                borderColor={useColorModeValue(
                  'blackAlpha.200',
                  'whiteAlpha.200'
                )}
              />
            </InputGroup>
          </FormControl>
          <FormControl id="phone" isInvalid={!!errors?.phone} w="full">
            <FormLabel fontSize={'12px'} lineHeight="normal">
              <HStack>
                <Text as="span">
                  {t('contact-sales.form.phone')} ({t('common.labels.optional')}
                  )
                </Text>
                <FormErrorMessage
                  mt={0}
                  fontSize={'xs'}
                  textTransform="lowercase"
                >
                  {errors.phone && errors.phone.message}
                </FormErrorMessage>
              </HStack>
            </FormLabel>
            <InputGroup size="md">
              <Input
                type="tel"
                placeholder={'(123) 456-7890'}
                {...register('phone', {
                  pattern: {
                    value: PHONE_NUMBER_PATTERN,
                    message: t('forms.errors.valid-phone-number'),
                  },
                })}
                bg={useColorModeValue('blackAlpha.200', 'whiteAlpha.200')}
                borderColor={useColorModeValue(
                  'blackAlpha.200',
                  'whiteAlpha.200'
                )}
              />
            </InputGroup>
          </FormControl>
        </Flex>

        {/* SMS Agreement Checkbox - only show if phone is entered */}
        {phoneValue.trim() !== '' && (
          <Box w="100%">
            <FormControl id="smsAgreement" isInvalid={!!errors?.smsAgreement}>
              <FormLabel fontSize={'12px'} lineHeight="normal">
                <Checkbox
                  {...register('smsAgreement', {
                    required: t('forms.errors.phone-required'),
                  })}
                  isChecked={watch('smsAgreement', false)}
                  onChange={(e) => {
                    register('smsAgreement').onChange(e)
                  }}
                  color={useColorModeValue('blackAlpha.700', 'whiteAlpha.700')}
                >
                  <Text as="span" fontSize="xs" lineHeight={1.33}>
                    {t.rich('contact-sales.form.sms-agreement', {
                      privacyPolicyUrl: (children) => (
                        <Link
                          href="/privacy-policy"
                          isExternal
                          color={useColorModeValue(
                            'blackAlpha.700',
                            'whiteAlpha.700'
                          )}
                          textDecoration="underline"
                        >
                          {children}
                        </Link>
                      ),
                      termsUrl: (children) => (
                        <Link
                          href="/terms-of-service"
                          isExternal
                          color={useColorModeValue(
                            'blackAlpha.700',
                            'whiteAlpha.700'
                          )}
                          textDecoration="underline"
                        >
                          {children}
                        </Link>
                      ),
                    })}
                  </Text>
                </Checkbox>
                <FormErrorMessage mt={2} fontSize={'xs'} ml="26px">
                  {errors.smsAgreement && errors.smsAgreement.message}
                </FormErrorMessage>
              </FormLabel>
            </FormControl>
          </Box>
        )}

        <Turnstile
          siteKey={TURNSTILE_KEY}
          onSuccess={() => setCaptchaSuccess(true)}
        />
        <Box w="100%">
          <Button
            mt={0}
            size="lg"
            w="100%"
            variant="brand"
            h={'41'}
            fontWeight={500}
            onClick={handleSubmit(onSubmit)}
            isDisabled={!captchaSuccess || loading}
          >
            {loading ? <Spinner /> : t('buttons.submit')}
          </Button>
        </Box>
      </VStack>
      <ContactSalesModal isOpen={isOpen} onClose={onClose} />
    </Box>
  )
}
